/**
 * Adjusts a given date to account for the local timezone offset.
 *
 * This function takes a date in various formats (Date object, number, or string) and adjusts it to account for the
 * local timezone offset, returning a new Date object representing the adjusted time. If the input is a string or number,
 * it first converts it to a Date object. For strings or numbers representing seconds since the epoch, it multiplies by 1000
 * to convert to milliseconds for the Date constructor.
 *
 * @param {Date | number | string} date - The date to adjust. Can be a Date object, a UNIX timestamp in seconds (number),
 *                                        or a string that can be parsed into a Date.
 * @param useOffset {boolean} - Whether to adjust the time for the local timezone offset.
 * @returns {Date} A new Date object representing the time adjusted for the local timezone offset.
 */
export const getDateWithOffset = (date: Date | number | string, useOffset: boolean = true): Date => {
  let _date: Date
  if (typeof date === 'string' || typeof date === 'number') {
    _date = String(date).length === 10 ? new Date(parseInt(String(date), 10) * 1000) : new Date(date)
  } else {
    _date = date
  }
  if (!useOffset) {
    return _date
  }

  const timezoneOffsetInMinutes = _date.getTimezoneOffset()
  const timezoneOffsetInSeconds = timezoneOffsetInMinutes * 60

  const dateSeconds = _date.getTime() / 1000
  const dateWithOffset = dateSeconds + timezoneOffsetInSeconds

  return new Date(dateWithOffset * 1000)
}
